import {
  formatFrenchPhoneNumber,
  getAddressLabel,
  getGmapsLink,
} from "@ac-expo/utils";
import {
  EnvelopeIcon,
  FacebookLogoIcon,
  Icon,
  InstagramLogoIcon,
  LinkedinLogoIcon,
  LinkSimpleIcon,
  MapPinIcon,
  PhoneIcon,
  XLogoIcon,
  YoutubeLogoIcon,
} from "@phosphor-icons/react";
import { noop } from "@tanstack/react-table";
import { AnchorHTMLAttributes, ComponentProps } from "react";
import StructureDetails, { CondensedStructureType } from "./StructureDetails";

export const StructureContactSection = ({
  structure,
  onWebsiteClick = noop,
}: Pick<
  ComponentProps<typeof StructureDetails>,
  "structure" | "onWebsiteClick"
>) => {
  return (
    <StructureLinksSection
      structure={structure}
      onWebsiteClick={onWebsiteClick}
    />
  );
};

const StructureLink = ({
  Icon,
  label,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  Icon: Icon;
  label: string;
}) => (
  <div className="flex items-center gap-1">
    <Icon size={20} className="text-secondary-500" />
    <a
      className="text-sm text-slate-600 font-semibold underline"
      target="_blank"
      rel="noreferrer"
      {...props}
    >
      {label}
    </a>
  </div>
);

const StructureLinksSection = ({
  structure,
  onWebsiteClick = noop,
}: {
  structure: CondensedStructureType;
  onWebsiteClick?: () => void;
}) => {
  const { phoneNumber, businessEmail, socials, address } = structure;

  const addressLabel = getAddressLabel(structure.address);
  const formattedPhoneNumber = formatFrenchPhoneNumber(phoneNumber ?? "");

  return (
    <div className="flex flex-col gap-2 overflow-hidden">
      {addressLabel && (
        <StructureLink
          Icon={MapPinIcon}
          label={addressLabel}
          href={getGmapsLink(address)}
        />
      )}
      {businessEmail && (
        <StructureLink
          Icon={EnvelopeIcon}
          label={businessEmail}
          href={`mailto:${businessEmail}`}
        />
      )}
      {formattedPhoneNumber && (
        <StructureLink
          Icon={PhoneIcon}
          label={formattedPhoneNumber}
          href={`tel:${formattedPhoneNumber}`}
        />
      )}
      {socials?.websiteUrl && (
        <StructureLink
          Icon={LinkSimpleIcon}
          label={socials.websiteUrl}
          href={socials.websiteUrl}
          onClick={onWebsiteClick}
        />
      )}
    </div>
  );
};

export const StructureSocialsSection = ({
  structure,
  onSocialsClick,
}: {
  structure: CondensedStructureType;
  onSocialsClick?: () => void;
}) => {
  return (
    <div className="flex w-full gap-4 md:gap-4 md:w-auto md:items-start">
      {structure?.socials?.facebookUrl && (
        <a
          href={structure.socials.facebookUrl}
          target="_blank"
          rel="noreferrer"
          onClick={onSocialsClick}
        >
          <FacebookLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
      {structure?.socials?.twitterUrl && (
        <a
          href={structure.socials.twitterUrl}
          target="_blank"
          rel="noreferrer"
          onClick={onSocialsClick}
        >
          <XLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
      {structure?.socials?.instagramUrl && (
        <a
          href={structure.socials.instagramUrl}
          target="_blank"
          rel="noreferrer"
          onClick={onSocialsClick}
        >
          <InstagramLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
      {structure?.socials?.youtubeUrl && (
        <a
          href={structure.socials.youtubeUrl}
          target="_blank"
          rel="noreferrer"
          onClick={onSocialsClick}
        >
          <YoutubeLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
      {structure?.socials?.linkedinUrl && (
        <a
          href={structure.socials.linkedinUrl}
          target="_blank"
          rel="noreferrer"
          onClick={onSocialsClick}
        >
          <LinkedinLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
    </div>
  );
};
